<template>
    <div data-testid="pm-review-row" class="flex items-center gap-2 py-3 border-b border-border last:border-0">
        <div class="grow min-w-0 flex flex-col sm:flex-row gap-1 sm:gap-4 sm:items-baseline">
            <span class="body-small-semibold" :class="labelClass">{{ review.cta?.label }}</span>
            <span v-if="dateRangeText" class="details text-ink-muted">{{ dateRangeText }}</span>
        </div>
        <!-- Manually completed with nothing to view: show non-interactive status text per prototype.
             Mirrors the button's px-4 inset and shrink-0 so it right-aligns consistently with the View/Start states. -->
        <span v-if="isMarkedCompleteOnly" class="flex items-center gap-2 py-2 border border-transparent text-ink-muted body-small whitespace-nowrap shrink-0">
            <i class="bi bi-check-lg text-sm text-notification-success-text" aria-hidden="true"></i>
            {{ t("moments.cta.markedComplete") }}
        </span>
        <BaseButton
            v-else
            type="button"
            :theme="isCompleted ? 'tertiary' : 'neutral'"
            size="sm"
            :disabled="buttonState.disabled"
            :aria-busy="isLoading"
            :aria-label="isLoading ? $t('moments.review.loading') : undefined"
            class="flex items-center gap-2.5 shrink-0"
            @click="handleChatClick">
            <i v-if="buttonState.beforeIcon" class="bi text-sm text-black/70" :class="buttonState.beforeIcon" aria-hidden="true"></i>
            <span v-if="buttonState.text" class="body-small text-start whitespace-nowrap">{{ buttonState.text }}</span>
            <div v-if="isLoading" class="flex flex-row">
                <!-- nbsp's here match the button height to the has-text case -->
                &nbsp;
                <LoadingSpinner class="w-4.5 h-4.5 mx-5" />
                &nbsp;
            </div>
            <i v-if="buttonState.afterIcon" class="bi text-sm text-black/70" :class="buttonState.afterIcon" aria-hidden="true"></i>
        </BaseButton>
        <BaseDropdownMenu v-if="isMenuAvailable" :sections="menuSections" @select="handleMenuSelect">
            <template #trigger>
                <BaseButton type="button" theme="tertiary" size="sm" icon-only :aria-label="t('moments.cta.actionsMenu')">
                    <i class="bi bi-three-dots" aria-hidden="true" />
                </BaseButton>
            </template>
        </BaseDropdownMenu>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import { formatCtaDateRange } from "~vue/composables/useCtaDateRange";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import type { DecoratedPerformanceReview } from "~vue/types/performance_management";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
    review: DecoratedPerformanceReview;
    isDisabled?: boolean;
    isMomentReadOnly?: boolean;
    // Section hasn't opened yet (start date in the future): disable input and show "Coming soon".
    isComingSoon?: boolean;
    isLoading?: boolean;
}>();

const emit = defineEmits(["cta-convo-clicked", "completion-toggled"]);

const isToggling = ref(false);
const isManuallyCompleted = computed(() => !!props.review.manually_completed_at);
const isChatCompleted = computed(() => props.review.conversation?.state === "completed");
const isCompleted = computed(() => isManuallyCompleted.value || isChatCompleted.value);
// Manually marked complete with no conversation yet: render plain "Marked complete" text instead of
// a "View" button (which would otherwise start a brand-new conversation). If a conversation exists
// — even one started and then marked complete midway — keep "View" so the user can return to it.
const isMarkedCompleteOnly = computed(() => isManuallyCompleted.value && !props.review.conversation && !props.isMomentReadOnly && !props.isComingSoon);

// The menu's only action is toggling completion, which isn't allowed on a read-only or not-yet-open
// moment, so the trigger is dropped rather than rendered inert.
const isMenuAvailable = computed(() => !props.isMomentReadOnly && !props.isComingSoon);

const menuSections = computed<DropdownMenuSection[]>(() => [
    {
        id: "actions",
        items: [
            { id: "toggle-completion", type: "button", label: isCompleted.value ? t("moments.cta.markIncomplete") : t("moments.cta.markComplete"), disabled: isToggling.value },
        ],
    },
]);

const labelClass = computed(() => {
    if (isCompleted.value) {
        return "text-ink-muted line-through";
    }
    if (props.isComingSoon || (props.isMomentReadOnly && !props.review.conversation)) {
        return "text-ink-muted";
    }
    return "";
});

const togglePayload = ref({ mark_complete: true });
const toggleUrl = computed(() => `/api/coach/performance_reviews/${props.review.id}/toggle_completion/`);
const { execute: executeToggle } = useFetch(toggleUrl, { immediate: false }).post(togglePayload).json();

const handleToggleCompletion = async () => {
    if (isToggling.value) {
        return;
    }
    isToggling.value = true;

    const willMarkComplete = !isManuallyCompleted.value;
    togglePayload.value = { mark_complete: willMarkComplete };

    try {
        await executeToggle(true);
        logUserInteraction("performance_review_manual_completion_toggled", {
            review_id: props.review.id,
            mark_complete: willMarkComplete,
        });
        emit("completion-toggled", props.review.id, willMarkComplete);
    } finally {
        isToggling.value = false;
    }
};

const handleMenuSelect = (item: DropdownMenuItem) => {
    if (item.id === "toggle-completion") {
        void handleToggleCompletion();
    }
};

const buttonState = computed<{
    text: string;
    disabled: boolean;
    beforeIcon?: string;
    afterIcon?: string;
}>(() => {
    if (props.isLoading) {
        return {
            text: "",
            disabled: true,
        };
    }

    // Section not open yet — keep the existing read-only button wording; the "coming soon"
    // messaging lives on the section's status pill, not in the button box.
    if (props.isComingSoon) {
        return {
            text: t("moments.cta.unavailable"),
            disabled: true,
        };
    }

    if (props.isMomentReadOnly) {
        if (props.review.conversation) {
            return {
                text: t("moments.cta.view"),
                disabled: props.isDisabled,
                beforeIcon: "bi-eye-fill",
            };
        }
        return {
            text: t("moments.cta.unavailable"),
            disabled: true,
        };
    }

    if (isCompleted.value) {
        return {
            text: t("moments.cta.view"),
            disabled: props.isDisabled,
            beforeIcon: "bi-eye-fill",
        };
    }

    if (props.review.conversation) {
        if (props.review.conversation.state === "in_progress") {
            return {
                text: t("moments.cta.continue"),
                disabled: props.isDisabled,
                afterIcon: "bi-arrow-right",
            };
        }
        return {
            text: t("moments.cta.view"),
            disabled: props.isDisabled,
            beforeIcon: "bi-eye-fill",
        };
    }

    return {
        text: t("moments.cta.start"),
        disabled: props.isDisabled,
        afterIcon: "bi-arrow-right",
    };
});

const dateRangeText = computed(() => {
    const cta = props.review.cta;
    if (!cta) {
        return "";
    }
    return formatCtaDateRange(cta.start_date || null, cta.due_date || null);
});

const handleChatClick = () => {
    emit("cta-convo-clicked", props.review.id);
};
</script>
